import { useLocation } from 'react-router-dom'

import {
  Badge,
  ExternalLinkIcon,
  HouseIcon,
  StartPageIcon,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { useAccount, useOrganizationId } from '~publish/contexts/AccountContext'
import { useIsLegacyPlan } from '~publish/contexts/AccountContext/hooks'
import { useCommunityCounter } from '~publish/contexts/useCommunityCounter'
import { buildStartPageHomeUrl } from '~publish/helpers/externalUrls'
import { formatCompactNumber } from '~publish/helpers/numberFormatters'
import { usePostCounts } from '~publish/pages/AllChannels/PostList/usePostCounts'
import { useHasAccessToBufferHome } from '~publish/hooks/useHasAccessToBufferHome'

import { MixedPublishAccessCoachMark } from '~publish/components/ViewOnlyCoachMarks'

import { SidebarNavigationList } from './SidebarNavigationList'
import type { SidebarNavigationItem } from './SidebarNavigationList'
import { BufferHomeAnnouncementCoachmark } from '../BufferHomeAnnouncementCoachmark'
import { useAllChannelsUrl } from '../../hooks/useAllChannelsUrl'
import { useCommentsUrl } from '../../hooks/useCommentsUrl'
import { useInsightsUrl } from '../../hooks/useInsightsUrl'
import { useChannels } from '../ChannelsSection/useChannels'
import { useOrganizationSwitcherContext } from '../OrganizationSwitcher/OrganizationSwitcherContext'
import { SecondChannelTourStepTwoCoachMark } from '../SecondChannelCelebration'

import {
  isCommunityPath,
  isCreatePath,
  isInsightsPath,
  MAIN_NAVIGATION_ITEMS,
} from './mainNavigationItems'

import styles from './MainNavigation.module.css'

import type { JSX } from 'react'

export function MainNavigation(): JSX.Element {
  const organizationId = useOrganizationId()
  const location = useLocation()
  const { channels } = useChannels()
  const { counts, loading: countsLoading } = usePostCounts({
    organizationId: organizationId ?? undefined,
  })
  const {
    total: communityCount,
    includesMentions: communityCountIncludesMentions,
    loading: communityCountLoading,
  } = useCommunityCounter()

  const { loading: organizationSwitcherLoading } =
    useOrganizationSwitcherContext()
  const { loading: accountLoading } = useAccount()

  // Single-channel orgs navigate through the channel's own sub-navigation in
  // the channels section; the top-level nav keeps only Home and Create.
  const isSingleChannel = channels.length === 1

  const allChannelsUrl = useAllChannelsUrl()
  const commentsUrl = useCommentsUrl()
  const insightsUrl = useInsightsUrl()

  const shouldShowCommentsBadge = communityCount > 0 && !communityCountLoading
  const queueCount = counts?.queue ?? 0
  const queueCountLimit = counts?.limit
  const isLegacyPlan = useIsLegacyPlan()
  const { hasAccess: hasAccessToHome, loading: homeAccessLoading } =
    useHasAccessToBufferHome()
  const isBufferHomeEnabled = !homeAccessLoading && hasAccessToHome
  const hasStartPageChannel = channels.some((ch) => ch.service === 'startPage')
  const isCreateActive = isCreatePath(location.pathname)
  const isCommunityActive = isCommunityPath(location.pathname)
  const isInsightsActive = isInsightsPath(location.pathname)

  const items: SidebarNavigationItem[] = []

  // The Home, Create, and Community items are gated on account data
  // (ownership and plan). While the account loads those gates read as
  // "hidden", so omitting the items here would make the nav grow by up to
  // three rows once the account resolves, shifting everything below it.
  // Hold each gated slot with a skeleton row until the gate is known.
  if (homeAccessLoading) {
    items.push({ key: 'home', skeleton: true })
  } else if (isBufferHomeEnabled) {
    items.push({
      key: 'home',
      label: 'Home',
      to: '/home',
      icon: <HouseIcon color="subtle" />,
      shortcutKey: 'H',
      decorator: (children) => (
        <BufferHomeAnnouncementCoachmark>
          {children}
        </BufferHomeAnnouncementCoachmark>
      ),
    })
  }

  if (accountLoading) {
    items.push({ key: 'create', skeleton: true })
  } else if (!isLegacyPlan) {
    items.push({
      key: 'create',
      ...MAIN_NAVIGATION_ITEMS.create,
      to: '/create/ideas',
      selected: isCreateActive,
      shortcutKey: 'I',
    })
  }

  items.push({
    key: 'publish',
    ...MAIN_NAVIGATION_ITEMS.publish,
    to: allChannelsUrl,
    shortcutKey: 'P',
    // The two coach marks can't fire together — connecting a second channel
    // implies posting access, which the mixed coach mark's gates exclude —
    // and each wrapper passes children through untouched when its gates fail.
    decorator: (children) => (
      <SecondChannelTourStepTwoCoachMark>
        <MixedPublishAccessCoachMark>{children}</MixedPublishAccessCoachMark>
      </SecondChannelTourStepTwoCoachMark>
    ),
    suffix: (
      <>
        {!countsLoading && !organizationSwitcherLoading && (
          <>
            <span className={styles.countText}>
              {formatCompactNumber(queueCount, queueCountLimit)}
            </span>
            <VisuallyHidden> posts</VisuallyHidden>
          </>
        )}
        {(countsLoading || organizationSwitcherLoading) && (
          <>
            &ensp;
            <VisuallyHidden>Post counts loading</VisuallyHidden>
          </>
        )}
      </>
    ),
  })

  if (accountLoading) {
    items.push({ key: 'community', skeleton: true })
  } else if (!isLegacyPlan) {
    items.push({
      key: 'community',
      ...MAIN_NAVIGATION_ITEMS.community,
      to: commentsUrl,
      selected: isCommunityActive,
      shortcutKey: 'C',
      suffix: (
        <>
          {(communityCountLoading || organizationSwitcherLoading) && (
            <>
              &ensp;
              <VisuallyHidden>Loading Community count</VisuallyHidden>
            </>
          )}
          {!communityCountLoading &&
            !organizationSwitcherLoading &&
            shouldShowCommentsBadge && (
              <Badge variant="brand" size="small">
                {formatCompactNumber(communityCount, 99)}
                <VisuallyHidden>
                  {communityCountIncludesMentions
                    ? ' unanswered comments and mentions'
                    : ' unreplied comments'}
                </VisuallyHidden>
              </Badge>
            )}
        </>
      ),
    })
  }

  items.push({
    key: 'insights',
    ...MAIN_NAVIGATION_ITEMS.insights,
    to: insightsUrl,
    selected: isInsightsActive,
    end: true,
    shortcutKey: 'A',
  })

  if (hasStartPageChannel) {
    items.push({
      key: 'start-page',
      label: 'Start Page',
      href: buildStartPageHomeUrl(),
      external: true,
      icon: <StartPageIcon color="subtle" />,
      shortcutKey: 'S',
      suffix: <ExternalLinkIcon size="xsmall" color="subtle" />,
    })
  }

  const visibleItems = isSingleChannel
    ? items.filter((item) => item.key === 'home' || item.key === 'create')
    : items

  return <SidebarNavigationList items={visibleItems} />
}
